import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Search, X, ArrowRight, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import API_BASE from '@/lib/API_BASE';



const POPULAR_TAGS = ['React', 'Python', 'Data Science', 'AI', 'Cloud', 'ServiceNow'];

export default function SearchOverlay({ onClose }) {
  const [query,   setQuery]   = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const inputRef  = useRef(null);
  const debounceRef = useRef(null);

  // Auto-focus on open
  useEffect(() => { inputRef.current?.focus(); }, []);

  // Close on Escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Debounced API call
  const fetchResults = useCallback((q) => {
    if (q.length < 2) {
      setResults([]);
      return;
    }

    // Clear previous debounce timer
    clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      setLoading(true);
      setError('');

      axios.get(`${API_BASE}/search.php`, { params: { q } })
        .then(res => {
          if (res.data.success) setResults(res.data.data);
          else setError('Search failed.');
        })
        .catch(() => setError('Server error.'))
        .finally(() => setLoading(false));
    }, 350); // 350ms debounce
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    fetchResults(val);
  };

  const handleTagClick = (tag) => {
    setQuery(tag);
    fetchResults(tag);
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setError('');
    inputRef.current?.focus();
  };

  // Highlight matched text
  const highlightMatch = (text) => {
    if (!query) return text;
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts  = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part)
        ? <mark key={i} className="bg-accent/30 text-foreground rounded px-0.5">{part}</mark>
        : part
    );
  };

  const showResults  = query.length >= 2;
  const showEmpty    = showResults && !loading && results.length === 0 && !error;
  const showPopular  = !showResults;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-foreground/60 backdrop-blur-sm flex items-start justify-center pt-24"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: -20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.95 }}
        className="bg-card rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 p-5 border-b">
          {loading
            ? <Loader2 className="w-5 h-5 text-primary animate-spin shrink-0" />
            : <Search className="w-5 h-5 text-muted-foreground shrink-0" />
          }
          <input
            ref={inputRef}
            value={query}
            onChange={handleChange}
            placeholder="Search courses, topics, skills..."
            className="flex-1 bg-transparent text-lg outline-none placeholder:text-muted-foreground/50"
          />
          {/* Clear button — only show when query has text */}
          {query && (
            <button
              onClick={handleClear}
              className="p-1 hover:bg-muted rounded-lg transition-colors"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1 hover:bg-muted rounded-lg transition-colors ml-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Results list */}
        {showResults && (
          <div className="max-h-96 overflow-y-auto p-2">

            {/* Error */}
            {error && (
              <p className="text-center text-sm text-red-500 py-6">{error}</p>
            )}

            {/* Loading skeleton */}
            {loading && (
              <div className="space-y-2 p-2">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-14 bg-muted animate-pulse rounded-lg" />
                ))}
              </div>
            )}

            {/* Results */}
            {!loading && results.length > 0 && (
              <>
                <p className="text-xs text-muted-foreground px-3 py-2 font-medium uppercase tracking-wide">
                  {results.length} result{results.length !== 1 ? 's' : ''} for "{query}"
                </p>
                {results.map(course => (
                  <Link
                    key={course.id}
                    to={`/course/${course.id}`}
                    onClick={onClose}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-muted transition-colors group"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">
                        {highlightMatch(course.title)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {course.level} · {course.duration}
                        {course.category_name && (
                          <span className="ml-2 px-1.5 py-0.5 bg-primary/10 text-primary rounded text-[10px] font-medium">
                            {course.category_name}
                          </span>
                        )}
                      </p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-3" />
                  </Link>
                ))}
              </>
            )}

            {/* Empty state */}
            {showEmpty && (
              <div className="p-8 text-center">
                <Search className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">No courses found for "{query}"</p>
                <p className="text-xs text-muted-foreground/60 mt-1">Try different keywords</p>
              </div>
            )}
          </div>
        )}

        {/* Popular searches */}
        {showPopular && (
          <div className="p-5">
            <p className="text-xs text-muted-foreground mb-3 font-medium uppercase tracking-wide">
              Popular Searches
            </p>
            <div className="flex flex-wrap gap-2">
              {POPULAR_TAGS.map(tag => (
                <button
                  key={tag}
                  onClick={() => handleTagClick(tag)}
                  className="px-3 py-1.5 rounded-full bg-muted text-sm text-muted-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        )}

      </motion.div>
    </motion.div>
  );
}